'use client'

import { useEffect, useRef } from 'react'
import { useIsomorphicLayoutEffect } from 'usehooks-ts'

/**
 * Calls `onEnter` when Enter is pressed anywhere on the page, e.g. to submit a form without a
 * `<form>` element. For a single element, `useOnEnter` gives an `onKeyDown` handler instead.
 *
 * @param enabled Listen only while true, e.g. only on the step of a flow that has the form.
 */
export function useEnterKeyPress(onEnter: () => void, enabled = true) {
    // Read on every key press, so the listener sees the latest callback without re-registering.
    // Updated after the render commits, not during it: a render React discards must not leak in.
    const onEnterRef = useRef(onEnter)
    useIsomorphicLayoutEffect(() => {
        onEnterRef.current = onEnter
    })

    useEffect(() => {
        if (!enabled) {
            return
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                onEnterRef.current()
            }
        }
        document.addEventListener('keydown', onKeyDown)

        return () => document.removeEventListener('keydown', onKeyDown)
    }, [enabled])
}
